import { BulkEditIcon, Button, Table, type TableColumns } from '@bp/ui-components';
import type { OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { niceDate } from 'utils/dateCalculations';
import { getProfileRoleTranslation } from 'utils/profileRoles';
import type { ProfileQueryType } from '../forms/ImportPersonsForm';
import type { ProcuratPerson } from '../hooks/useProcuratGroups';

export type ProcuratDepartmentPersonsTableData = ProcuratPerson & {
  lastImport: string;
  imported: boolean;
  profile: string;
  roles: string[];
  matching?: ProfileQueryType[] | undefined;
};

type ProcuratClassesTableProps = {
  data: ProcuratDepartmentPersonsTableData[];
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  onImport: () => Promise<void> | void;
};

export const ProcuratDepartmentPersonsTable = ({
  data,
  rowSelection,
  onRowSelectionChange,
  onImport,
}: ProcuratClassesTableProps) => {
  const { t } = useTranslation();

  // biome-ignore lint/correctness/useExhaustiveDependencies(t): ignore t
  const personColumns: TableColumns<ProcuratDepartmentPersonsTableData>[] = useMemo(() => {
    return [
      {
        header: t('common.name'),
        id: 'name',
        accessorKey: 'name',
        canExpand: true,
        cell: (cell) => {
          return `${cell.row.original.firstName} ${cell.row.original.lastName}`;
        },
      },
      {
        header: t('common.role'),
        id: 'role',
        accessorKey: 'roles',
        accessorFn: (row) => row.roles.map((r) => getProfileRoleTranslation(r)).join(', '),
        canExpand: true,
      },
      {
        id: 'lastImport',
        accessorKey: 'lastImport',
        header: t('common.lastUpdated'),
        cell: (cell) => {
          return niceDate(cell.row.original.lastImport, 'short', 'short') ?? '-';
        },
      },
      {
        id: 'imported',
        accessorKey: 'imported',
        header: t('common.imported'),
        type: 'boolean',
        size: 150,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Table<ProcuratDepartmentPersonsTableData>
      showActionBar
      actionBarSettings={{
        showSelectedRowsCount: true,
        showExpertFilter: false,
        globalFilterPlaceholder: t('common.name'),
        extendedActionsRight: (
          <Button
            hierarchy='tertiary'
            icon={<BulkEditIcon />}
            onClick={onImport}
            disabled={Object.keys(rowSelection).length < 1}
          >
            {t('procuratImport.importPersons')}
          </Button>
        ),
      }}
      columns={personColumns}
      data={data}
      onRowSelectionChange={onRowSelectionChange}
      rowSelection={rowSelection}
      showSelect
      customPadding='var(--spacing-7)'
    />
  );
};
